/**
 * The one place this client talks to the server.
 *
 * Every path goes through the dev proxy at `/api`, which forwards to the Express server; the
 * server in turn runs the Python theory facade. A failure carries the server's own message
 * because those messages name the thing that was wrong — an unknown example, an unseatable
 * instrument — and a generic "request failed" would throw that away.
 */

import type {
  ArrangeJob,
  ArrangeRequest,
  ArrangeStarted,
  AskPayload,
  AskRequest,
  CataloguePayload,
  ComparePayload,
  CountPayload,
  EvidencePayload,
  ExamplePayload,
  FiguresPayload,
  InstrumentsPayload,
  JobStarted,
  LibraryPayload,
  LibraryScorePayload,
  PicturesRequest,
  ReadersPayload,
  RecogniseJob,
  RulePayload,
  RulesPayload,
  SeatsPayload,
  UploadPayload,
  WritePayload,
  WriteRequest,
} from "@app/shared-types";

const BASE = "/api";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(
      response.status,
      `the server did not answer with JSON: ${text.slice(0, 200)}`,
    );
  }
  if (!response.ok) {
    const said = (payload as { error?: string } | null)?.error;
    throw new ApiError(response.status, said ?? `request failed with ${response.status}`);
  }
  return payload as T;
}

export const api = {
  catalogue: () => request<CataloguePayload>("/theory/catalogue"),
  example: (id: string) => request<ExamplePayload>(`/theory/examples/${encodeURIComponent(id)}`),
  compare: (id: string) =>
    request<ComparePayload>(`/theory/examples/${encodeURIComponent(id)}/compare`),
  rules: () => request<RulesPayload>("/theory/rules"),
  rule: (predicate: string) =>
    request<RulePayload>(`/theory/rules/${encodeURIComponent(predicate)}`),
  instruments: () => request<InstrumentsPayload>("/theory/instruments"),
  seats: () => request<SeatsPayload>("/theory/seats"),
  figures: () => request<FiguresPayload>("/theory/figures"),
  evidence: () => request<EvidencePayload>("/theory/evidence"),
  write: (body: WriteRequest) =>
    request<WritePayload>("/theory/write", { method: "POST", body: JSON.stringify(body) }),
  /** Send a score to be arranged, as raw bytes, and get back the id to arrange it by. */
  upload: (score: Blob) =>
    request<UploadPayload>("/theory/uploads", {
      method: "POST",
      body: score,
      headers: { "content-type": "application/octet-stream" },
    }),
  arrange: (body: ArrangeRequest) =>
    request<ArrangeStarted>("/theory/arrange", { method: "POST", body: JSON.stringify(body) }),
  /** Read a request in words into the roster and settings it asks for. Nothing is arranged. */
  ask: (body: AskRequest) =>
    request<AskPayload>("/theory/ask", { method: "POST", body: JSON.stringify(body) }),
  arrangeJob: (job: string) => request<ArrangeJob>(`/theory/arrange/${encodeURIComponent(job)}`),
  cancelArrange: (job: string) =>
    request<ArrangeJob>(`/theory/arrange/${encodeURIComponent(job)}`, { method: "DELETE" }),
  /**
   * Start reading an uploaded PDF of printed music into a score, by `reader` or, naming none,
   * the server's own reader where it has one, Audiveris otherwise.
   */
  recognise: (upload: string, reader?: string) =>
    request<JobStarted>("/theory/recognise", {
      method: "POST",
      body: JSON.stringify(reader === undefined ? { upload } : { upload, reader }),
    }),
  /** A reading; with `since`, what it has read so far too, where newer than the `since`-th. */
  recogniseJob: (job: string, since?: number) =>
    request<RecogniseJob>(
      `/theory/recognise/${encodeURIComponent(job)}${since === undefined ? "" : `?since=${since}`}`,
    ),
  cancelRecognise: (job: string) =>
    request<RecogniseJob>(`/theory/recognise/${encodeURIComponent(job)}`, { method: "DELETE" }),
  /** Uploaded photos and pictures of pages, and any PDF among them, made into one PDF. */
  pictures: async (body: PicturesRequest): Promise<Blob> => {
    const response = await fetch(`${BASE}/theory/pictures`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "content-type": "application/json" },
    });
    if (response.ok) return response.blob();
    // A refusal is the server's JSON, as every other's is.
    const said = await response
      .json()
      .then((payload: { error?: string } | null) => payload?.error)
      .catch(() => undefined);
    throw new ApiError(response.status, said ?? `request failed with ${response.status}`);
  },
  /** The machines that can read a scan, and which of them this server has been given. */
  readers: () => request<ReadersPayload>("/theory/readers"),
  /** Count an uploaded reading against the uploaded score it was read from. */
  count: (read: string, truth: string) =>
    request<CountPayload>("/theory/count", {
      method: "POST",
      body: JSON.stringify({ read, truth }),
    }),
  library: (query: {
    q: string;
    collection: string | null;
    instrument: string | null;
    offset: number;
  }) => {
    const params = new URLSearchParams({ q: query.q, offset: String(query.offset) });
    if (query.collection !== null) params.set("collection", query.collection);
    if (query.instrument !== null) params.set("instrument", query.instrument);
    return request<LibraryPayload>(`/library?${params.toString()}`);
  },
  libraryScore: (id: string) => request<LibraryScorePayload>(`/library/${encodeURIComponent(id)}`),
};
